// frontend/src/components/ProjectDashboard.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DocumentUpload from './DocumentUpload';
import EditableTitle from './EditableTitle';
import EditableProjectTitle from './EditableProjectTitle';
import projectService from '../services/project/projectService';
import documentService from '../services/document/documentService';
import '../styles/global.css';

const ProjectDashboard = () => {
    const { projectId } = useParams();
    const [documents, setDocuments] = useState([]);
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);
    
    // Hover states
    const [glossaryHover, setGlossaryHover] = useState(false);
    const [instructionsHover, setInstructionsHover] = useState(false);
    
    const navigate = useNavigate();

    useEffect(() => {
        const fetchProjectData = async () => {
            setLoading(true);
            setError(''); 
            setProject(null); 
            setDocuments([]); 
            
            try {
                // Fetch project details using project service
                const projectData = await projectService.getProjectById(projectId);
                
                if (projectData.error) {
                    setError(projectData.message || "Failed to load project");
                    setLoading(false);
                    return;
                }
                
                setProject(projectData);
                
                // Fetch documents using document service
                const documentsData = await documentService.getDocumentsByProject(projectId);
                
                if (documentsData.error) {
                    setError(documentsData.message || "Failed to load documents");
                } else {
                    setDocuments(Array.isArray(documentsData) ? documentsData : []);
                }
            } catch (err) {
                console.error("Error fetching project data:", err);
                setError("An unexpected error occurred while loading project data");
            } finally {
                setLoading(false);
            }
        };

        fetchProjectData();
    }, [projectId]);

    const handleDocumentUpload = (newDocument) => {
        setDocuments([...documents, newDocument]);
        setSuccessMessage(`Document "${newDocument.title}" uploaded successfully`);

        // Clear success message after a few seconds
        setTimeout(() => {
            setSuccessMessage('');
        }, 3000);
    };

    const handleDeleteClick = (doc, e) => {
        e.stopPropagation(); // Prevent navigation to document view
        setDocToDelete(doc);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (!docToDelete) return;

        setLoading(true);
        setError('');

        try {
            // Use document service to delete the document
            const result = await documentService.deleteDocument(docToDelete.id);
            
            if (result.error) {
                throw new Error(result.message || "Failed to delete document");
            }

            // Remove the document from the state
            setDocuments(documents.filter(doc => doc.id !== docToDelete.id));
            setSuccessMessage(`Document "${docToDelete.title}" deleted successfully`);

            // Reset confirmation state
            setDocToDelete(null);
            setShowDeleteConfirm(false);

            // Clear success message after a few seconds
            setTimeout(() => {
                setSuccessMessage('');
            }, 3000);
        } catch (err) {
            console.error("Error deleting document:", err);
            setError(err.message || "Failed to delete document");
        } finally {
            setLoading(false);
        }
    };

    const cancelDelete = () => {
        setDocToDelete(null);
        setShowDeleteConfirm(false);
    };

    const handleTitleUpdate = (docId, updatedDoc) => {
        // Update the document in the local state
        setDocuments(
            documents.map(doc =>
                doc.id === docId ? { ...doc, ...updatedDoc } : doc
            )
        );
    };

    // Handle updating the project title
    const handleProjectTitleUpdate = (updatedProject) => {
        setProject({...project, ...updatedProject});
    };

    const navigateToDocument = (docId) => {
        navigate(`/documents/${docId}`);
    };

    // Common button styles
    const baseButtonStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.5rem 1rem',
        border: '1px solid #475569',
        borderRadius: '0.25rem',
        color: '#475569',
        fontWeight: '500',
        fontSize: '0.875rem',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        minWidth: 'max-content',
        transition: 'all 0.2s ease'
    };

    // Hover styles
    const hoverButtonStyle = {
        ...baseButtonStyle,
        backgroundColor: '#f1f5f9', 
        borderColor: '#4f46e5',     
        color: '#4f46e5'            
    };

    if (loading && !documents.length && !project) {
        return (
            <div className="container">
                <div className="card text-center p-8">
                    <div className="loading">Loading project...</div>
                </div>
            </div>
        );
    }

    if (error && !documents.length && !project) {
        return (
            <div className="container">
                <div className="card text-center p-4 text-red-500">{error}</div>
            </div>
        );
    }

    return (
        <div className="container">
            <div className="back-button" onClick={() => navigate('/')}>
                ← Back to Projects
            </div>

            {project && (
                <div className="flex justify-between items-center mb-6">
                    <EditableProjectTitle
                        projectId={projectId}
                        initialTitle={project.title}
                        onUpdate={handleProjectTitleUpdate}
                        fontSize="text-2xl"
                        className="mr-2"
                    />
                    <div className="badge badge-primary">
                        {project.source_language} → {project.target_language}
                    </div>
                </div>
            )}

            {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}
            {successMessage && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">{successMessage}</div>}

            {/* FIXED BUTTONS SECTION WITH HOVER EFFECTS */}
            <div className="mb-6" style={{ overflow: 'hidden' }}>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
                    <button
                        onClick={() => navigate(`/projects/${projectId}/glossary`)}
                        onMouseEnter={() => setGlossaryHover(true)}
                        onMouseLeave={() => setGlossaryHover(false)}
                        style={glossaryHover ? hoverButtonStyle : baseButtonStyle}
                    >
                        <svg 
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ marginRight: '8px' }}
                        >
                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                        </svg>
                        Manage Glossary
                    </button>
                    <button
                        onClick={() => navigate(`/projects/${projectId}/instructions`)}
                        onMouseEnter={() => setInstructionsHover(true)}
                        onMouseLeave={() => setInstructionsHover(false)}
                        style={instructionsHover ? hoverButtonStyle : baseButtonStyle}
                    >
                        <svg 
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ marginRight: '8px' }}
                        >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                            <line x1="16" y1="13" x2="8" y2="13"></line>
                            <line x1="16" y1="17" x2="8" y2="17"></line>
                            <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                        Edit Instructions
                    </button>
                </div>
            </div>

            <div className="card mb-6">
                <h2 className="text-xl font-semibold mb-4">Add New Document</h2>
                <DocumentUpload
                    projectId={projectId}
                    onUpload={handleDocumentUpload}
                />
            </div>

            <div className="card">
                <h2 className="text-xl font-semibold mb-4">Project Documents</h2>
                {loading && !Array.isArray(documents) ? (
                    <div className="text-center p-8">
                        <div className="loading">Loading documents...</div>
                    </div>
                ) : Array.isArray(documents) && documents.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(Array.isArray(documents) ? documents : []).map(doc => (
                            <div
                                key={doc.id}
                                className="card hover:shadow-lg"
                            >
                                <div className="flex justify-between items-center mb-2">
                                    <EditableTitle
                                        documentId={doc.id}
                                        initialTitle={doc.title}
                                        onUpdate={(updatedDoc) => handleTitleUpdate(doc.id, updatedDoc)}
                                        fontSize="text-lg"
                                        className="flex-grow mr-2"
                                    />
                                    <button
                                        onClick={(e) => handleDeleteClick(doc, e)}
                                        className="btn-icon danger flex-shrink-0"
                                        title="Delete document"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>
                                <div className="badge badge-primary">
                                    {doc.file_type?.toUpperCase()}
                                </div>
                                <div className="mt-4 text-right">
                                  <a 
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      navigateToDocument(doc.id);
                                    }}
                                    className="nav-link"
                                  >
                                    Work on this document
                                  </a>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center p-6 bg-gray-50 rounded">
                        <p className="text-gray-500">No documents added yet. Upload a document to start translation.</p>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="modal-backdrop">
                    <div className="modal-content">
                        <h3 className="text-lg font-semibold mb-4">Confirm Deletion</h3>
                        <p className="mb-6">
                            Are you sure you want to delete the document <span className="font-semibold">{docToDelete?.title}</span>?
                            This will also delete all translation batches associated with this document.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={cancelDelete}
                                className="btn btn-outline"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="btn btn-danger"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectDashboard;